import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { IsolationVoltage } from '../fields/IsolationVoltage';

import { AbstractPort, Port } from '../fields/AbstractPort';

import { synchronize, synchronizePowerFlowDirection } from '../helpers';

import { ComponentDefinition } from '../../types/ComponentDefinition';

const port = AbstractPort.extend({
    AC: Port.AC.default({}),
    DC: Port.DC.default({}),
    overvoltageCategory: z.number().optional(),
}).default({
    powerFlowDirection: 'bidirectional',
});

const electrical = z
    .object({
        ports: z.tuple([port, port]).default([undefined, undefined]),
        isolationVoltage: IsolationVoltage.validator,
    })
    .default({});

const MeterBase = z.object({
    type: z.literal('meter').default('meter'),
    electrical: synchronize(synchronizePowerFlowDirection(electrical), {
        'ports.0': ['ports.1'],
    }),
});

export const MeterValidator = ComponentValidator.merge(MeterBase);
export const MeterPayloadValidator = ComponentPayloadValidator.merge(MeterBase);

export const MeterDefinition: ComponentDefinition = {
    type: 'meter',
    name: 'Meter',
    plural: 'Meters',
    indicator: 'P',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: true,
    ports: {
        min: 2,
        max: 2,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
        displayAsSinglePort: true,
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: MeterValidator,
        payload: MeterPayloadValidator,
    },
};

export type Meter = z.infer<typeof MeterValidator>;
export type MeterPayload = z.infer<typeof MeterPayloadValidator>;
